import { forwardRef, useState } from 'react';

import Icon from '../Icon/Icon';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

/**
 * PopOverコンポーネント内のオプションを表します。
 */
interface Option {
  /** ラベル。 */
  label: string;
  /** 強調表示するかどうか。 */
  highlight?: boolean;
  /** actionが無効かどうか。 */
  disabled?: boolean;
  /** クリックされたときに実行されるアクション。 */
  action: () => void;
}

/**
 * PopOverコンポーネントのプロパティ。
 */
interface PopOverProps {
  /** ポップオーバーに表示するオプションのリスト。 */
  options: Option[];
  /** ポップオーバートリガーボタンのラベル。 */
  label?: string;
}

/**
 * オプションのリストをポップオーバー内に表示するPopOverコンポーネント。
 *
 * @param {PopOverProps} props - コンポーネントのプロパティ。
 * @returns {JSX.Element} レンダリングされたPopOverコンポーネント。
 */
const PopOver = forwardRef<HTMLDivElement, PopOverProps>(
  ({ options, label }, ref) => {
    const [open, setOpen] = useState(false);
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {label ? (
            <Button variant="outline" aria-label={label}>
              {label}
            </Button>
          ) : (
            <Button variant="ghost" size="icon" aria-label="Options">
              <Icon iconName="MoreHorizontalIcon" className="h-5 w-5" />
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent ref={ref} className="w-48 p-2">
          {options.map((option) => (
            <Button
              key={option.label}
              onClick={() => {
                option.action();
                setOpen(false);
              }}
              disabled={option.disabled}
              variant="ghost"
              className={`w-full justify-start ${option.highlight ? 'text-error-OnNeutral' : ''}`}
            >
              {option.label}
            </Button>
          ))}
        </PopoverContent>
      </Popover>
    );
  },
);

PopOver.displayName = 'PopOver';

export default PopOver;
